import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {useCan, usePersona} from 'src/permissions/hooks';
import {FC, useState} from 'react';
import {Box} from '@mui/material';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {columnConfigUsers} from './UsersTable.tableConfig';
import {UsersTableActions} from '../UsersTableAction';
import {UsersTableProps} from './UsersTable.types';
import {UsersSortFieldEnum} from 'src/enums';
import {UsersFilterValue} from '../UsersTableAction/UsersTableActions.types';
import {UsersSearchRequestData} from 'src/types';
import {UserTypeEnum} from 'src/enums/common';
import {UserItemCard} from './UserItemCard';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {useDefineIsRealEstateRole} from 'src/app/hooks';

export const UsersTable: FC<UsersTableProps> = ({
                                                    data,
                                                    size,
                                                    pageNumber,
                                                    handleUpdateTableData,
                                                    handleChangePage,
                                                    handleChangeRowsNumber,
                                                    title,
                                                    onAddClick,
                                                    buttonText,
                                                    onlyTitle,
                                                    icon,
                                                    filters,
                                                }) => {
    // Seeded from the filter the page holds, not from empty: a remount is what coming back from a
    // detail page is, and starting at '' meant the next filter change wiped the search that was
    // applied - `updateData` writes `keyword: search`, so an empty local copy cleared the URL.
    const [searchValue, setSearchValue] = useState<string>(filters.keyword ?? '');
    const [sortValue, setSortValue] = useState<GridSortItem>({field: '', sort: 'asc'});

    const persona = usePersona();
    const isRealEstate = useDefineIsRealEstateRole();
    const manageUsers = useCan('users.delete');
    const canDeleteUser = manageUsers;

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue, filters));
    };

    // The filter control still calls it a role in the interface; what it narrows by is the kind of
    // person, which is the only thing a single-select over a directory can mean - and it is the axis
    // the strip's type tiles are on, so choosing one here clears a gap chosen there. Same
    // alternatives-not-combinations rule, applied from the other control.
    const handleFilter = (value: UsersFilterValue) => {
        handleUpdateTableData(updateData(searchValue, sortValue, {
            ...filters,
            notActivated: undefined,
            contractExists: undefined,
            contractIsSigned: undefined,
            userTypes: value.role ? [value.role as UserTypeEnum] : undefined,
        }));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0], filters));
    };

    /** A merge onto the filter the list is on, not a fresh object built from the toolbar's one field. */
    const updateData = (
        search: string,
        sort: GridSortItem,
        filter: UsersSearchRequestData
    ): UsersSearchRequestData => ({
        ...filter,
        keyword: search,
        sortAsc: sort ? sort.sort === 'asc' : true,
        sortField: (sort && sort.field) || UsersSortFieldEnum.EMAIL,
    });

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <UsersTableActions
                        filters={filters}
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        handleSearch={handleSearch}
                        handleFilter={handleFilter}
                        totalElements={data.page.totalElements}
                    />
                )}
                totalPages={data.page.totalPages}
                page={pageNumber && pageNumber === 0 ? pageNumber : data.page.number}
                pageSize={size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
            <ResponsiveTable
                responsiveElement={
                    <CardList>
                        {data.content.map((item) => (
                            <UserItemCard
                                key={item?.id}
                                {...item}
                                canDeleteUser={canDeleteUser}
                            />
                        ))}
                    </CardList>
                }
                desktopElement={
                    <Table
                        rows={data.content}
                        hideFooter
                        columns={columnConfigUsers(canDeleteUser, persona.isRealEstate || (persona.isBackOffice && !persona.isBusinessOwner))}
                        handleSort={handleSort}
                    />
                }
            />
            </TableLayout>
        </Box>
    );
};
